/**
 * Facility Storage Location Site Map – shared types.
 *
 * Mirrors the Tier II facility site map (see tierii-shared/types/site-map.ts) but is scoped to
 * one map per facility (not per year) and centred on RCRA storage-area classification instead of
 * chemical inventory: every `facilityStorageLocations` row (central accumulation area, satellite
 * accumulation area, permitted storage, or other) can be placed as a marker on the map, alongside
 * generic site-plan elements (building outline, entrances, pathways, evacuation routes).
 */

import type { FacilityID, FacilityStorageAreaType } from '../../cross-app-shared/types/db.ts';
import type { ISO8601Timestamp } from '../../cross-app-shared/types/common.ts';
import type { FacilityStorageLocationID } from './db.ts';

/** Storage-area pins: always a Point, always linked to a `facilityStorageLocations` row. */
export type StorageSiteMapStorageAreaFeatureType =
  | 'central-accumulation-area' // RCRA 90-day central accumulation area
  | 'satellite-accumulation-area' // RCRA satellite accumulation area
  | 'permitted-storage-area' // permitted hazardous waste storage
  | 'other-storage-area'; // other storage location (SPCC tank, Tier II vessel, etc.)

/** Unlinked site-plan symbols, always a Point. */
export type StorageSiteMapSymbolFeatureType =
  | 'entrance' // door or entrance (incl. emergency exits)
  | 'water-supply' // water supply for firefighting (e.g. fire hydrant), per 40 CFR
  // 262.262(c)(6) "Quick Reference Guide" element: locations of water supply.
  | 'notification-system'; // on-site notification system (e.g. fire alarm, smoke alarm),
// per 40 CFR 262.262(c)(7).

/** Always a LineString. */
export type StorageSiteMapLineFeatureType =
  | 'pathway' // access route to a storage location
  | 'evacuation-route'; // exit path for first responders / facility personnel

/** Always a Polygon. */
export type StorageSiteMapAreaFeatureType =
  | 'building-outline' // outer footprint of a building or structure
  | 'room'; // interior room / area boundary

/** Every feature drawn on the storage-location site map has one of these types. */
export type StorageSiteMapFeatureType =
  | StorageSiteMapStorageAreaFeatureType
  | StorageSiteMapSymbolFeatureType
  | StorageSiteMapLineFeatureType
  | StorageSiteMapAreaFeatureType;

/** Maps a storage location's RCRA `areaType` to its site-map feature type. */
export const AREA_TYPE_TO_FEATURE_TYPE: Record<FacilityStorageAreaType, StorageSiteMapStorageAreaFeatureType> = {
  'central-accumulation': 'central-accumulation-area',
  satellite: 'satellite-accumulation-area',
  'permitted-storage': 'permitted-storage-area',
  other: 'other-storage-area',
};

export type StorageSiteMapPointGeometry = { type: 'Point'; coordinates: [number, number] };
export type StorageSiteMapLineGeometry = { type: 'LineString'; coordinates: [number, number][] };
export type StorageSiteMapPolygonGeometry = { type: 'Polygon'; coordinates: [number, number][][] };
export type StorageSiteMapGeometry =
  | StorageSiteMapPointGeometry
  | StorageSiteMapLineGeometry
  | StorageSiteMapPolygonGeometry;

export interface StorageSiteMapStorageAreaProperties {
  featureType: StorageSiteMapStorageAreaFeatureType;
  /** Human-readable label shown on the map and in the legend. */
  label: string | null;
  /** Independently placed label center, in GeoJSON order [longitude, latitude]; null anchors it to the geometry. */
  labelPosition: [number, number] | null;
  /** Links the storage-area marker back to its `facilityStorageLocations` row. */
  facilityStorageLocationID: FacilityStorageLocationID;
}

export interface StorageSiteMapUnlinkedProperties<T extends StorageSiteMapFeatureType> {
  featureType: T;
  /** Human-readable label shown on the map and in the legend. */
  label: string | null;
  /** Independently placed label center, in GeoJSON order [longitude, latitude]; null anchors it to the geometry. */
  labelPosition: [number, number] | null;
}

/**
 * A single GeoJSON feature. Each feature type has exactly the geometry it may be drawn with, and only
 * storage-area pins link to a storage location.
 */
export type StorageSiteMapFeature =
  | { type: 'Feature'; geometry: StorageSiteMapPointGeometry; properties: StorageSiteMapStorageAreaProperties }
  | {
      type: 'Feature';
      geometry: StorageSiteMapPointGeometry;
      properties: StorageSiteMapUnlinkedProperties<StorageSiteMapSymbolFeatureType>;
    }
  | {
      type: 'Feature';
      geometry: StorageSiteMapLineGeometry;
      properties: StorageSiteMapUnlinkedProperties<StorageSiteMapLineFeatureType>;
    }
  | {
      type: 'Feature';
      geometry: StorageSiteMapPolygonGeometry;
      properties: StorageSiteMapUnlinkedProperties<StorageSiteMapAreaFeatureType>;
    };

/** Properties carried by every StorageSiteMap GeoJSON feature. */
export type StorageSiteMapFeatureProperties = StorageSiteMapFeature['properties'];

/** The full GeoJSON FeatureCollection stored for a facility's storage-location site map. */
export interface StorageSiteMapFeatureCollection {
  type: 'FeatureCollection';
  features: StorageSiteMapFeature[];
}

/** The full storage-location site map record (mirrors the DB row). */
export interface FacilityStorageLocationSiteMapRecord {
  facilityID: FacilityID;
  centerLat: number;
  centerLng: number;
  zoom: number;
  features: StorageSiteMapFeatureCollection;
  /** The row is stamped on every write, so a loaded map always has one. */
  updatedAt: ISO8601Timestamp;
}

/** A storage location available to place on the map (drag-and-drop source). */
export interface StorageSiteMapLocationItem {
  facilityStorageLocationID: FacilityStorageLocationID;
  name: string;
  areaType: FacilityStorageAreaType | null;
  isHazWasteRegulated: boolean;
  isSPCCRegulated: boolean;
  isTier2Regulated: boolean;
}

/** Default colours by feature type (used by the editor). */
export const STORAGE_SITE_MAP_COLORS: Record<StorageSiteMapFeatureType, string> = {
  'central-accumulation-area': '#dc2626', // red-600
  'satellite-accumulation-area': '#d97706', // amber-600
  'permitted-storage-area': '#7c3aed', // violet-600
  'other-storage-area': '#0891b2', // cyan-600
  'water-supply': '#0284c7', // sky-600
  'notification-system': '#ea580c', // orange-600
  pathway: '#2563eb', // blue-600
  'evacuation-route': '#16a34a', // green-600
  'building-outline': '#374151', // gray-700
  room: '#6b7280', // gray-500
  entrance: '#059669', // emerald-600
};

/** Human-readable labels for each feature type (used in legend/sidebar). */
export const STORAGE_SITE_MAP_LABELS: Record<StorageSiteMapFeatureType, string> = {
  'central-accumulation-area': 'Central Accumulation Area (90-day)',
  'satellite-accumulation-area': 'Satellite Accumulation Area',
  'water-supply': 'Water Supply (e.g. Fire Hydrant)',
  'notification-system': 'On-Site Notification System',
  'permitted-storage-area': 'Permitted Storage Area',
  'other-storage-area': 'Other Storage Location',
  pathway: 'Access Pathway',
  'evacuation-route': 'Evacuation Route',
  'building-outline': 'Building Outline',
  room: 'Room / Area',
  entrance: 'Entrance / Doorway',
};
